import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import type { SettlementStatus } from '@ip/shared-types';

interface SettlementBadgeProps {
  status: SettlementStatus;
  isOverdue?: boolean;
  daysOverdue?: number;
  /** Outstanding balance in the invoice currency — shown for 'partial'. */
  outstanding?: number;
  currency?: string | null;
  className?: string;
}

/**
 * Task A3: derived settlement of a sale invoice as a badge.
 * Precedence: overdue (rose, most urgent — with days) wins over partial
 * (amber, with the remaining amount) wins over unpaid (neutral red) /
 * paid (emerald). An overdue-partial invoice shows the overdue badge with
 * the outstanding amount appended so no information is lost.
 */
export function SettlementBadge({
  status,
  isOverdue,
  daysOverdue,
  outstanding,
  currency,
  className,
}: SettlementBadgeProps) {
  const outstandingLabel =
    outstanding !== undefined && outstanding > 0
      ? t('sales.settlement.outstandingLeft', {
          amount: `${formatPLN(outstanding)} ${currency || 'PLN'}`,
        })
      : null;

  if (status !== 'paid' && isOverdue) {
    return (
      <div className={cn('flex flex-col items-start gap-0.5', className)}>
        <Badge
          variant="outline"
          className="border-rose-500/40 bg-rose-500/10 font-normal text-rose-700 dark:text-rose-400"
        >
          {t('sales.settlement.overdueDays', { days: daysOverdue ?? 0 })}
        </Badge>
        {status === 'partial' && outstandingLabel && (
          <span className="text-muted-foreground text-[11px]">{outstandingLabel}</span>
        )}
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <Badge
        variant="outline"
        className={cn(
          'border-emerald-500/40 bg-emerald-500/10 font-normal text-emerald-700 dark:text-emerald-400',
          className,
        )}
      >
        {t('sales.settlement.paid')}
      </Badge>
    );
  }

  if (status === 'partial') {
    return (
      <div className={cn('flex flex-col items-start gap-0.5', className)}>
        <Badge
          variant="outline"
          className="border-amber-500/40 bg-amber-500/10 font-normal text-amber-700 dark:text-amber-400"
        >
          {t('sales.settlement.partial')}
        </Badge>
        {outstandingLabel && (
          <span className="text-muted-foreground text-[11px]">{outstandingLabel}</span>
        )}
      </div>
    );
  }

  return (
    <Badge
      variant="outline"
      className={cn(
        'border-red-500/40 bg-red-500/10 font-normal text-red-700 dark:text-red-400',
        className,
      )}
    >
      {t('sales.settlement.unpaid')}
    </Badge>
  );
}
